/** venture/CoLaneMatrix.tsx — 8단계부터 회사별로 갈라지는 진행을 한눈에 (2026-09-24)
 *  행 = 회사, 열 = 8~15단계, 칸 = 완료 ✓ / 진행 ● / 대기 ○. 칸을 누르면 그 단계가 열리고 그 회사 카드로 이동한다.
 *  컨설턴트의 첫 질문 "이 회사가 지금 어디까지 왔나"에 답하는 표. 타임라인의 「현재」(가장 늦은 회사 기준)를 보완한다. */
import React, { useEffect, useState } from 'react';
import { STEPS } from './types';
import { cosOf, coStat, coStep, coDone, SPLIT_FROM, LAST } from './costep';
import type { Project } from './types';
import { hasTwin, removeCoCard } from './coRemove';

const SHORT: Record<number, string> = { 8: '사채등기', 9: '확인신청', 10: '심사비', 11: '송부', 12: '출력', 13: 'VICS보고', 14: '투자확인서', 15: '수수료' };

/** onPatch · toast 를 주면 줄마다 「삭제」(두 번 눌러 확인 · 2026-11-02 「왜 3줄이야 · 하나 지우는 기능」) */
interface Props { p: Project; onOpen: (n: number, coId: string) => void; onPatch?: (patch: Partial<Project>) => void; toast?: (m: string) => void; }

export default function CoLaneMatrix({ p, onOpen, onPatch, toast }: Props) {
  const cos = cosOf(p);
  const [arm, setArm] = useState('');   // 한 번 누른 줄 — 3초 안에 한 번 더 누르면 삭제
  useEffect(() => { if (!arm) return undefined; const t = window.setTimeout(() => setArm(''), 3000); return () => window.clearTimeout(t); }, [arm]);
  const del = (id: string, name: string) => {
    if (!onPatch) return;
    if (arm !== id) { setArm(id); return; }
    setArm('');
    const patch = removeCoCard(p, id);
    if (!patch) return;
    onPatch(patch);
    if (toast) toast(`「${name || '법인'}」 줄을 지웠어요`);
  };
  if (!cos.length || p.step < SPLIT_FROM) return null;
  const cols: number[] = [];
  for (let n = SPLIT_FROM; n <= LAST; n++) cols.push(n);
  return (
    <div className="lane">
      <div className="lane-h"><b>회사별 진행</b><span className="s3muted">8단계부터는 회사마다 따로 갑니다 · 칸을 누르면 이동</span></div>
      <div className="lane-scroll">
        <table className="lanetbl">
          <thead>
            <tr><th className="co">투자법인</th>{cols.map((n) => <th key={n} title={STEPS[n - 1][0]}><span className="n">{n}</span>{SHORT[n] || STEPS[n - 1][0]}</th>)}{onPatch ? <th className="del" /> : null}</tr>
          </thead>
          <tbody>
            {cos.map((co) => {
              const cur = coDone(p, co.id) ? LAST + 1 : coStep(p, co.id);
              return (
                <tr key={co.id}>
                  <td className="co"><b>{co.name || '(법인)'}{hasTwin(cos, co) ? <span className="pill p-amber lane-twin" title="같은 회사가 한 줄 더 있어요 — 필요 없는 줄을 지우세요">중복</span> : null}</b><span className="s3muted">{coDone(p, co.id) ? '전체 완료' : cur < SPLIT_FROM ? '8단계 대기' : (cur + '단계 ' + (SHORT[cur] || ''))}</span></td>
                  {cols.map((n) => {
                    const st = coStat(p, co.id, n);
                    return (
                      <td key={n}>
                        <button type="button" className={'lcell ' + st} title={(co.name || '법인') + ' · ' + STEPS[n - 1][0] + ' · ' + (st === 'done' ? '완료' : st === 'cur' ? '진행' : '대기')} onClick={() => onOpen(n, co.id)}>
                          {st === 'done' ? '✓' : st === 'cur' ? '●' : '○'}
                        </button>
                      </td>
                    );
                  })}
                  {onPatch ? (
                    <td className="del">
                      <button type="button" className={'lane-del' + (arm === co.id ? ' on' : '')} onClick={() => del(co.id, co.name)}
                        title={arm === co.id ? '한 번 더 누르면 이 회사 줄을 지워요' : '이 회사 줄 지우기 — 회사별 진행 · 8~15단계 카드에서 빠져요'}>
                        {arm === co.id ? '한 번 더' : '삭제'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
